import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getAttachmentImageUrl } from "../../services/api";
import { Attachment } from "../../interfaces/feature-info";
import { ImageUrl } from "../img-swiper/img-swiper";
import styles from "./slider.module.scss";
import deleteButton from "../../images/delete-document.svg";
import placeholderImg from "../../images/slider-placeholder.png";

interface UploadedImage {
  file: File;
  id: string;
}

interface SliderProps {
  images: Attachment[];
  uploadedImages: UploadedImage[];
  onDeleteImg: (arg: number) => void;
  onDeleteUploadedImg: (arg: string) => void;
  attachmentsToDelete: number[];
  showDeleteButton: boolean;
}

const Slider: React.FC<SliderProps> = ({
  images,
  uploadedImages,
  onDeleteImg,
  onDeleteUploadedImg,
  attachmentsToDelete,
  showDeleteButton,
}) => {
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const [imageUrls, setImageUrls] = useState<ImageUrl[]>([]);

  const hasAnyImages = imageUrls.length > 0 || uploadedImages.length > 0;

  useEffect(() => {
    let isMounted = true;

    const fetchImageUrls = async () => {
      if (images && images.length > 0) {
        const acceptedImages: ImageUrl[] = [];

        for (const img of images) {
          try {
            const result = await getAttachmentImageUrl(img.id, true);
            if (
              typeof result === "object" &&
              "url" in result &&
              "id" in result
            ) {
              acceptedImages.push({
                url: result.url,
                id: result.id,
                isPlaceholder: false,
              });
            }
          } catch (error) {
            console.error(error);
          }
        }

        if (isMounted) {
          setImageUrls(acceptedImages);
        }
      }
    };

    fetchImageUrls();

    return () => {
      isMounted = false;
      imageUrls.forEach((image) => {
        if (image.url.startsWith("blob:")) {
          URL.revokeObjectURL(image.url);
        }
      });
    };
  }, [images]);

  return (
    <div style={{ width: "100%", height: "112px" }}>
      <div ref={containerRef} className={styles.slider}>
        {hasAnyImages ? (
          <>
            {imageUrls.map((img) => (
              <div className={styles.imgWrapper}>
                <img
                  key={img.url}
                  src={img.url}
                  alt={t("slider.slide.alt")}
                  className={styles.image}
                  style={{
                    opacity: attachmentsToDelete.includes(img.id!) ? 0.3 : 1,
                  }}
                />
                {!attachmentsToDelete.includes(img.id!) && showDeleteButton && (
                  <button>
                    <img
                      src={deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteImg(img.id!);
                      }}
                    />
                  </button>
                )}
              </div>
            ))}
            {uploadedImages.map((img) => (
              <div className={styles.imgWrapper}>
                <img
                  key={img.id}
                  src={URL.createObjectURL(img.file)}
                  alt={t("slider.slide.alt")}
                  className={styles.image}
                />
                {showDeleteButton && (
                  <button aria-label={t("slider.deleteButton.ariaLabel")}>
                    <img
                      src={deleteButton}
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteUploadedImg(img.id);
                      }}
                    />
                  </button>
                )}
              </div>
            ))}
          </>
        ) : (
          <div className={styles.imgWrapper}>
            <img
              style={{ objectFit: "initial" }}
              className={styles.image}
              src={placeholderImg}
              alt={t("slider.placeholder.alt")}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default Slider;
